/**
 * THE animation evaluator (plan D11).
 *
 * Editor preview, playback, the runtime SDK and GLB baking all go through this module so the
 * three never drift apart. Pure: no DOM, no editor state, no `three` object graph.
 */

import { applyEasing } from './easing';
import { identityQuat, normalizeQuat, slerpTuple } from './quat';
import type {
  BakedClipSamples,
  BoneRotationTrack,
  CanonicalAnimationClip,
  CanonicalBoneId,
  Pose,
  QuaternionTuple,
} from './types';

/** Index of the last keyframe whose frame is <= `frame`, or -1 when `frame` precedes all keys. */
function findKeyIndex(track: BoneRotationTrack, frame: number): number {
  const keys = track.keyframes;
  let lo = 0;
  let hi = keys.length - 1;
  let result = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (keys[mid].frame <= frame) {
      result = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return result;
}

function writeQuat(out: QuaternionTuple, src: QuaternionTuple): QuaternionTuple {
  out[0] = src[0];
  out[1] = src[1];
  out[2] = src[2];
  out[3] = src[3];
  return out;
}

/**
 * Evaluate a single bone track at a (possibly fractional) frame.
 * Before the first key → first key's rotation; after the last → last key's rotation;
 * empty track → identity.
 */
export function evaluateTrack(
  track: BoneRotationTrack,
  frame: number,
  out?: QuaternionTuple,
): QuaternionTuple {
  const target = out ?? identityQuat();
  const keys = track.keyframes;

  if (keys.length === 0) return writeQuat(target, [0, 0, 0, 1]);
  if (keys.length === 1) return writeQuat(target, keys[0].rotation);

  const i = findKeyIndex(track, frame);
  if (i < 0) return writeQuat(target, keys[0].rotation);
  if (i >= keys.length - 1) return writeQuat(target, keys[keys.length - 1].rotation);

  const a = keys[i];
  const b = keys[i + 1];
  const span = b.frame - a.frame;
  if (span <= 0) return writeQuat(target, b.rotation);

  const t = (frame - a.frame) / span;
  const eased = applyEasing(a.easing, t);
  return slerpTuple(a.rotation, b.rotation, eased, target);
}

/**
 * Evaluate every track of a clip at a (possibly fractional) frame.
 * When `out` is supplied it is reused: entries for bones the clip does not animate are removed.
 */
export function evaluatePose(
  clip: CanonicalAnimationClip,
  frame: number,
  out?: Pose,
): Pose {
  const target: Pose = out ?? {};

  if (out) {
    for (const key of Object.keys(out) as CanonicalBoneId[]) {
      if (!clip.tracks[key]) delete out[key];
    }
  }

  for (const key of Object.keys(clip.tracks) as CanonicalBoneId[]) {
    const track = clip.tracks[key];
    if (!track) continue;
    const slot = target[key];
    target[key] = evaluateTrack(track, frame, slot);
  }

  return target;
}

// ---------------------------------------------------------------------------
// Hips vertical offset (additive scalar channel)
//
// The channel lives on `Hips` keyframes and is SPARSE: a key may carry no `hipsY` at all, because
// every key authored before the channel existed has none and because the editor only writes it
// when the user actually moved the hips. Treating a missing value as 0 would make a pre-existing
// rotation key yank a crouch back to bind height mid-way, so the functions below skip such keys
// and interpolate between the nearest keys that DO carry a value.
// ---------------------------------------------------------------------------

/** True when any Hips key carries a finite `hipsY`. Allocation-free (runs on the runtime path). */
export function clipHasHipsY(clip: CanonicalAnimationClip): boolean {
  const track = clip.tracks.Hips;
  if (!track) return false;
  const keys = track.keyframes;
  for (let i = 0; i < keys.length; i++) {
    const value = keys[i].hipsY;
    if (typeof value === 'number' && Number.isFinite(value)) return true;
  }
  return false;
}

function hasHipsY(key: { hipsY?: number }): boolean {
  return typeof key.hipsY === 'number' && Number.isFinite(key.hipsY);
}

/**
 * The normalized Hips offset at a (possibly fractional) frame — see `RotationKeyframe.hipsY`.
 *
 * Interpolates between the nearest Hips keys at or before and after `frame` that carry the field,
 * with the START key's easing (the same rule `evaluateTrack` uses for rotations, so the offset and
 * the pose bend together). Before the first such key → its value; after the last → its value; a
 * clip with no Hips track or no `hipsY` key at all → 0, which is bind height.
 *
 * Allocation-free: binary search then a walk outward. This runs once per take per frame in the
 * runtime SDK, where a per-frame allocation is a GC pause in a game loop.
 */
export function evaluateHipsY(clip: CanonicalAnimationClip, frame: number): number {
  const track = clip.tracks.Hips;
  if (!track) return 0;
  const keys = track.keyframes;
  if (keys.length === 0) return 0;

  const at = findKeyIndex(track, frame);

  // Nearest key at or before `frame` that carries the field.
  let prev = at;
  while (prev >= 0 && !hasHipsY(keys[prev])) prev--;

  // Nearest key strictly after `frame` that carries the field.
  let next = at + 1;
  while (next < keys.length && !hasHipsY(keys[next])) next++;

  if (prev < 0) return next < keys.length ? (keys[next].hipsY as number) : 0;
  if (next >= keys.length) return keys[prev].hipsY as number;

  const a = keys[prev];
  const b = keys[next];
  const span = b.frame - a.frame;
  if (span <= 0) return b.hipsY as number;

  const from = a.hipsY as number;
  const to = b.hipsY as number;
  const eased = applyEasing(a.easing, (frame - a.frame) / span);
  return from + (to - from) * eased;
}

/** `evaluateHipsY` at a wall-clock time, wrapped exactly like `evaluateAtTime`. */
export function evaluateHipsYAtTime(
  clip: CanonicalAnimationClip,
  seconds: number,
  loop: boolean,
): number {
  return evaluateHipsY(clip, wrapFrame(clip, timeToFrame(clip, seconds), loop));
}

/**
 * Map an arbitrary frame into the clip's playable range.
 * Looping treats the clip as N frames long, so frame N is frame 0 again.
 * Non-looping clamps to [0, durationFrames].
 */
export function wrapFrame(
  clip: CanonicalAnimationClip,
  frame: number,
  loop: boolean,
): number {
  const duration = clip.durationFrames;
  if (!Number.isFinite(frame)) return 0;
  if (duration <= 0) return 0;
  if (!loop) return Math.min(duration, Math.max(0, frame));
  const wrapped = frame % duration;
  return wrapped < 0 ? wrapped + duration : wrapped;
}

export function timeToFrame(clip: CanonicalAnimationClip, seconds: number): number {
  return seconds * clip.fps;
}

export function frameToTime(clip: CanonicalAnimationClip, frame: number): number {
  return clip.fps === 0 ? 0 : frame / clip.fps;
}

/** Clip duration in seconds. */
export function clipDurationSeconds(clip: CanonicalAnimationClip): number {
  return frameToTime(clip, clip.durationFrames);
}

export function evaluateAtTime(
  clip: CanonicalAnimationClip,
  seconds: number,
  loop: boolean,
  out?: Pose,
): Pose {
  return evaluatePose(clip, wrapFrame(clip, timeToFrame(clip, seconds), loop), out);
}

/**
 * One sample per integer frame 0..durationFrames inclusive, for every bone that has a track.
 * Feeds GLB baking (plan D9): the exporter writes LINEAR quaternion tracks from these samples,
 * so the exported animation matches the editor exactly whatever easing was authored.
 */
export function sampleClipForBake(clip: CanonicalAnimationClip): BakedClipSamples {
  const frameCount = Math.max(0, Math.floor(clip.durationFrames)) + 1;
  const times = new Float32Array(frameCount);
  for (let f = 0; f < frameCount; f++) times[f] = frameToTime(clip, f);

  const rotations: BakedClipSamples['rotations'] = {};
  const scratch = identityQuat();

  for (const key of Object.keys(clip.tracks) as CanonicalBoneId[]) {
    const track = clip.tracks[key];
    if (!track) continue;
    const buffer = new Float32Array(frameCount * 4);
    for (let f = 0; f < frameCount; f++) {
      evaluateTrack(track, f, scratch);
      normalizeQuat(scratch, scratch);
      buffer[f * 4 + 0] = scratch[0];
      buffer[f * 4 + 1] = scratch[1];
      buffer[f * 4 + 2] = scratch[2];
      buffer[f * 4 + 3] = scratch[3];
    }
    rotations[key] = buffer;
  }

  const samples: BakedClipSamples = { times, rotations };

  // Only when the clip actually uses the channel: a rotation-only clip must bake to exactly what it
  // baked before `hipsY` existed, and an all-zero array is not the same answer as "no channel".
  if (clipHasHipsY(clip)) {
    const offsets = new Float32Array(frameCount);
    for (let f = 0; f < frameCount; f++) offsets[f] = evaluateHipsY(clip, f);
    samples.hipsY = offsets;
  }

  return samples;
}
